import { toast as sonnerToast, Toaster as SonnerToaster, type ExternalToast } from "sonner";

export interface ToasterProps {
  position?: "top-right" | "top-center" | "bottom-right" | "bottom-center";
}

/**
 * Renders queued toasts in the 99 Problems style. Mount it once at the app
 * root. The app already mounts one in App.tsx; use this in isolated roots
 * such as Storybook or embedded widgets.
 */
export function Toaster({ position = "bottom-right" }: ToasterProps) {
  return (
    <SonnerToaster
      theme="dark"
      position={position}
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast:
            "!rounded-[var(--radius-lg)] !border !border-ds-gold-primary/30 !bg-ds-black-secondary/95 !backdrop-blur-xl !shadow-ds-gold !font-body",
          title: "!font-heading !tracking-wide",
          description: "!text-ds-text-muted",
        },
      }}
    />
  );
}

type ToastOptions = Pick<ExternalToast, "description" | "duration" | "action" | "id">;

/**
 * Show a notification: `toast.success("Deck saved")`,
 * `toast.error("Build failed", { description: "…" })`.
 */
export const toast = {
  show: (message: string, options?: ToastOptions) => sonnerToast(message, options),
  info: (message: string, options?: ToastOptions) => sonnerToast.info(message, options),
  success: (message: string, options?: ToastOptions) => sonnerToast.success(message, options),
  warning: (message: string, options?: ToastOptions) => sonnerToast.warning(message, options),
  error: (message: string, options?: ToastOptions) => sonnerToast.error(message, options),
  loading: (message: string, options?: ToastOptions) => sonnerToast.loading(message, options),
  promise: sonnerToast.promise,
  dismiss: sonnerToast.dismiss,
};
